import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable, of } from "rxjs";

import { AsyncControls, AsyncStorage } from "@core/services/AsyncStorage";
import { DirectSalesData, Sale, SalesData, SaleLocationData } from "@core/data/sales";
import { APIResponse } from '@shared/types.barrel';
import { ClientsService, Client } from '@admin/clients/clients.service';
import { DebugService as debug } from "@core/services/debug.service";
import { expand, map, reduce } from 'rxjs/operators';
import { AuthService } from '@core/services/auth/auth.service';

export { AsyncStorage, DirectSalesData, Sale, SalesData, SaleLocationData }

const SaleIdPrefix = "subscriptionSale"

const Endpoint = {
    Add: "subscriptionSales/add",
    Get: "subscriptionSales/get",
    Update: "subscriptionSales/update",
}

@Injectable()
export class SubscriptionSalesService {

    public saleIdPrefix: string = SaleIdPrefix;
    private _subscriptionSales!: AsyncStorage<Sale>;
    private _targetClientId!: string;
    private _activeClientId!: string;
    private _lastUsedId: number = 0;
    private _savedSaleIds: string[] = [];

    private _isInitialConfiguration: boolean = false;
    private _eTag: string | null = null;

    constructor(
        private _http: HttpClient,
        private _clientsService: ClientsService,
        private _auth: AuthService
    ) {
        this._initializeStorage();
    }

    private _initializeStorage() {

        //Prepare location storage
        this._subscriptionSales = new AsyncStorage(this._http);
        this._subscriptionSales._setAddFunction(this._saveSaleConfiguration());
        this._subscriptionSales._setElementComparisonFunction(this._saleComparison);
        this._subscriptionSales._setSaveActiveElementFunction(this._saveSaleConfiguration());
        this._subscriptionSales._setUpdateFunction(this._listSales());

        this._clientsService.clients.onActiveElementChange()
            .subscribe((client: Client) => {
                this._targetClientId = client.clientId;
                this._subscriptionSales.update();
            });

    }

    public get sales() { return this._subscriptionSales };
    public get saleIds() { return (this._subscriptionSales.data.value ?? []).map((sale: Sale) => sale.saleId) }
    public get nextId(): string { return this._lastUsedId++, this.saleIdPrefix + this._lastUsedId; };
    public get activeClientId(): string { return this._activeClientId };
    public get savedItemIds(): string[] { return this._savedSaleIds };

    private _saleComparison(a: Sale | null, b: Sale | null): boolean {

        //Compare the two
        return a && b ? a.saleId === b.saleId : false;

    }

    private _saveSaleConfiguration(): (_: any, options: any) => Observable<any> {
        const self = this;
        return (_: any, options: any): Observable<any> => {

            let saleConfiguration: DirectSalesData = new DirectSalesData();

            //Update the fields
            saleConfiguration.clientId = self._targetClientId || self._auth.activeClient?.clientId;
            saleConfiguration.salesData.lastId = this._lastUsedId;

            //Add sales
            let sales: Sale[] = self._subscriptionSales.data.value ?? [];
            for (let sale of sales) {
                const { saleId } = sale
                this._prepareSaleForSaving(sale);
                saleConfiguration.salesData.data[saleId] = sale
            }

            //Add the ETag header if available
            const headers: Record<string, string> = {};
            if (self._eTag)
                headers["If-Match"] = self._eTag;

            //Select the appropriate endpoint
            let endpoint = Endpoint.Update
            if (this._isInitialConfiguration)
                endpoint = Endpoint.Add

            //Load all products
            debug.log(`Updating subscription sales configuration - endpoint: ${endpoint}, etag: ${this._eTag ?? "-"}`);
            return this._http
                .post<APIResponse<DirectSalesData>>(endpoint, saleConfiguration, { headers, observe: "response" })
                .pipe(
                    map((res: HttpResponse<APIResponse<DirectSalesData>>) => {

                        //Handle errors
                        const errors = res.body?.errors
                        if (errors?.length) {
                            throw new Error(errors.pop())
                        }

                        //Copy the etag value
                        const etag = res.headers.get("ETag")
                        if (etag)
                            this._eTag = etag

                        //Upon a valid response the initial configuration has been created
                        if (this._isInitialConfiguration)
                            this._isInitialConfiguration = false;

                        //Parse the sales
                        const updatedSales: Sale[] = [];
                        const saleMap = res.body?.data?.salesData?.data;
                        for (let saleId in saleMap) {
                            updatedSales.push(<Sale>saleMap?.[saleId])
                        }

                        //Store the last used id
                        this._lastUsedId = res.body?.data?.salesData?.lastId ?? this._lastUsedId;

                        return updatedSales
                    })
                )
        }
    }

    private _listSales(): (options: any, asyncControls?: AsyncControls) => Observable<Sale[]> {

        const self = this;
        return (options: any, asyncControls?: AsyncControls): Observable<Sale[]> => {

            //Load all products
            const clientId = options?.clientId || this._targetClientId || this._auth.activeClient?.clientId
            return this._http
                .post<APIResponse<DirectSalesData>>(Endpoint.Get, { clientId }, { observe: "response" })
                .pipe(
                    // expand keeps calling until condition fails
                    expand((res: HttpResponse<APIResponse<DirectSalesData>>) => {

                        //Check for errors
                        const errors = res.body?.errors
                        if (errors?.length)
                            throw new Error(errors[0])

                        //Check if there are more products to load
                        const lastEvaluatedKey = (<any>res).body?.data.lastEvaluatedKey
                        if (!lastEvaluatedKey)
                            return of()

                        return self._http.post<APIResponse<DirectSalesData>>(Endpoint.Get, { clientId, lastEvaluatedKey }, { observe: "response" })

                    }),
                    map((res: HttpResponse<APIResponse<DirectSalesData>>) => {
                        if (asyncControls?.activeClient)
                            asyncControls.activeClient.next(this._targetClientId)

                        //Update the local client id record
                        this._activeClientId = res.body?.data?.clientId ?? this._targetClientId;

                        //Copy the etag value
                        const etag = res.headers.get("etag")
                        if (etag)
                            this._eTag = etag

                        //Check if this is the initial configuration for products
                        const isClientId = !!res.body?.data?.clientId
                        if (!isClientId) this._isInitialConfiguration = true;

                        //Parse the sales
                        const updatedSales: Sale[] = [];
                        const saleMap = res.body?.data?.salesData?.data;
                        for (let saleId in saleMap) {
                            updatedSales.push(<Sale>saleMap?.[saleId])
                        }

                        //Store the last used id
                        this._lastUsedId = res.body?.data?.salesData?.lastId ?? this._lastUsedId;

                        //Update the saved sales
                        this._savedSaleIds = updatedSales.map((sale) => sale.saleId)

                        debug.log(`Loaded subscription sales:`, { clientId, etag });

                        return updatedSales
                    }),
                    reduce((all, current) => all.concat(current), [] as Sale[])
                );
        }

    }

    private _prepareSaleForSaving(sale: Sale) {

        //Remove unnecessary fields
        if (sale.supportedLocales) delete sale.supportedLocales


        //Ensure qty is a string
        //!This is not my fault, just maintaining behaviour until sales are updated
        if (sale.productReleaseFrequency?.day) {
            if ("string" != typeof sale.productReleaseFrequency.day.qty) {
                sale.productReleaseFrequency.day.qty = String(sale.productReleaseFrequency.day.qty);
            }
        }

        if (sale.productReleaseFrequency?.week) {
            if ("string" != typeof sale.productReleaseFrequency.week.qty) {
                sale.productReleaseFrequency.week.qty = String(sale.productReleaseFrequency.week.qty);
            }
        }
        if (sale.productReleaseFrequency?.month) {
            if ("string" != typeof sale.productReleaseFrequency.month.qty) {
                sale.productReleaseFrequency.month.qty = String(sale.productReleaseFrequency.month.qty);
            }
        }

    }


}

